"use client";

/**
 * What the live trail strip opens: the trail the client is on, or nearest to,
 * with where they are on it -- how far done, how far left and how much still
 * to climb, the track on a map with their position, the elevation profile
 * marked at the same place -- then the way to add a photo to the event's
 * album, the trail's facilities and the way to A minha inscrição. Away from
 * the trail the figures give way to how far it is, with the way to its start.
 *
 * Opened with openLiveTrail() from anywhere; the host is mounted once in the
 * shell. Everything it draws is already in the store (liveTrail.ts): it asks
 * the API for nothing, so it opens where there is no signal -- only the map's
 * own tiles need one, and the track is sketched under the map for when they
 * do not come.
 */
import { usePathname, useRouter } from "next/navigation";
import { ChevronRight, LocationEdit, MapPinOff } from "lucide-react";
import { inscricaoPath } from "@/lib/address";
import { eventHasStarted } from "@/lib/eventTime";
import { km, metres } from "@/lib/format";
import { Sheet } from "@/components/ui/Sheet";
import { ElevationProfile, SlopeLegend } from "@/components/event/ElevationProfile";
import { TrailFacilities } from "@/components/event/TrailPopup";
import { mapsHref } from "@/components/event/maps";
import { closeLiveTrail, distanceText, liveView, useLiveTrail, type LiveView } from "@/components/live/liveTrail";
import { LiveMap } from "@/components/live/LiveMap";
import { LivePhotos } from "@/components/live/LivePhotos";
import { trailTitle } from "@/components/event/labels";

export function LiveTrailHost() {
  const state = useLiveTrail();
  const view = liveView(state);
  const shown = view && view.kind !== "ask" ? view : null;
  return (
    <Sheet open={state.open && !!shown} onClose={closeLiveTrail} title={(shown && trailTitle(shown.live.trail)) || "Trilho"} width={495} maxHeight="88vh">
      {shown && <LiveTrailBody view={shown} />}
    </Sheet>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 flex-1 rounded-[10px] bg-ground px-3 py-2">
      <small className="block text-[11.5px] text-ink-soft">{label}</small>
      <b className="block truncate text-[16px] font-bold">{value}</b>
    </div>
  );
}

function Notice({ title, text }: { title: string; text?: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-[10px] bg-brand-soft px-3 py-2.5">
      <MapPinOff size={18} className="shrink-0 text-[#3d6b4b]" />
      <div className="leading-tight">
        <b className="block text-[14px] font-bold text-brand-dark">{title}</b>
        {text && <span className="block text-[12.5px] text-[#3d6b4b]">{text}</span>}
      </div>
    </div>
  );
}

function LiveTrailBody({ view }: { view: Exclude<LiveView, { kind: "ask" }> }) {
  const router = useRouter();
  const pathname = usePathname();
  const { day, live } = view;
  const { trail, measured } = live;
  const on = view.kind === "on";
  const along = on ? view.place.located.along : null;
  const start = mapsHref(trail.google_maps_link, trail.starting_point);
  const inscricao = inscricaoPath(day.event);
  const hasHeights = measured.climbed !== null;

  return (
    <div className="flex flex-col gap-4 px-4 pb-6">
      {view.kind === "locating" && <p className="rounded-[10px] bg-ground px-3 py-2.5 text-[14px] text-ink-soft">A procurar a tua posição…</p>}
      {view.kind === "away" && <Notice title="Estás fora do trilho" text={`A ${distanceText(view.place.located.distance)} do trilho`} />}
      {on && live.oneWay && <Notice title="Estás no trilho" />}
      {on && !live.oneWay && (
        <div className="flex gap-2">
          <Figure label="Feito" value={km(view.place.located.along / 1000)} />
          <Figure label="Falta" value={km(Math.max(0, measured.length - view.place.located.along) / 1000)} />
          {view.place.climbLeft !== null && <Figure label="Por subir" value={metres(view.place.climbLeft)} />}
        </div>
      )}

      <div>
        <LiveMap measured={measured} fix={view.kind === "locating" ? null : view.fix} located={on ? view.place.located : null} />
        {!on && start && (
          <a href={start} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-brand">
            <LocationEdit size={15} /> Navegar até ao início{trail.trailhead ? ` · ${trail.trailhead}` : ""}
          </a>
        )}
      </div>

      {hasHeights && (
        <div>
          <h4 className="mb-1.5 text-[14px] font-bold">Perfil de elevação</h4>
          <div className="h-14 w-full overflow-hidden rounded-[10px] border border-line bg-surface">
            <ElevationProfile track={trail.track} className="h-full" at={along} />
          </div>
          <SlopeLegend className="mt-1.5" />
        </div>
      )}

      {/* public-api takes an event's photos from its start on */}
      {eventHasStarted(day.event) && <LivePhotos eventId={day.event.id} />}

      <TrailFacilities facilities={trail.facilities} />

      {pathname !== inscricao && (
        <button
          type="button"
          onClick={() => {
            closeLiveTrail();
            router.push(inscricao);
          }}
          className="pressable inline-flex items-center justify-center gap-1 self-center py-1 text-[14.5px] font-bold text-brand"
        >
          A minha inscrição <ChevronRight size={17} />
        </button>
      )}
    </div>
  );
}
